import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { Col, Row } from 'antd';

import useBenguide from 'hooks/benguide';
import EditableHotspot from 'components/EditableHotspot/EditableHotspot';
import SavePlanDescriptionModal from 'components/SavePlanDescriptionModal/SavePlanDescriptionModal';

import useUPClient from 'modules/clients/UPClient/useUPClient';
import PlanCardEditor from 'modules/benefitsWellness/components/PlanCardEditor';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import {
  fetchHolidayAndTimeOffPlanList,
  getRichMediaContent,
  reloadBenefitAndTimeOffSection,
  updateRichMediaContent,
} from 'modules/holidaysAndTimeOff/slices/holidayAndTimeOffSlice';
import ContentPreviewer from 'components/ContentPreviewer/ContentPreviewer';
import Spinner from 'components/Spinner/Spinner';
import { BENEFIT_KIND } from 'constants/commonConstants';
import {
  HOLIDAY_AND_TIMEOFF_CHANNEL,
  OPEN_HOLIDAY_AND_TIMEOFF_EDIT_MODAL,
} from 'modules/clients/UPClient/UPClientConst';
import IconEdit from 'images/icon-edit.svg';
import WorkLifePlan from 'model/WorkLifePlan';
import AddYourText from 'modules/benefitsWellness/components/AddYourText/AddYourText';
import BenefitCarrier from 'model/BenefitCarrier';
import { SectionName } from 'modules/home/constants';
import { benefitCategory } from 'modules/auth/constants/commonConstants';
import ViewResourcesModal from 'components/ViewResourcesModal/ViewResourcesModal';
import { ReactComponent as LeftArrowIcon } from 'images/icons/Icon-left-arrow.svg';
import { ReactComponent as RightArrowIcon } from 'images/icons/Icon-right-arrow.svg';
import { getText } from 'utils/stringUtil';
import { PLAN_RESOURCES } from 'constants/languageConstants';
import styles from './holidayAndTimeOffCard.module.less';

type HolidayAndTimeOffCardProps = {
  plan: WorkLifePlan;
  moveBottom: () => void;
  moveTop: () => void;
  hideTop: boolean;
  hideBottom: boolean;
  renderReorder: boolean;
  dataIndex?: number;
};

const PLAN_DOCUMENT = 'PLAN_DOCUMENT';

const HolidayAndTimeOffCard: FC<HolidayAndTimeOffCardProps> = ({
  plan,
  moveBottom,
  moveTop,
  hideTop,
  hideBottom,
  renderReorder,
  dataIndex,
}: HolidayAndTimeOffCardProps) => {
  const upClient = useUPClient();
  const benguide = useBenguide();
  const dispatch = useAppDispatch();
  const { richMedia } = useAppSelector((state) => state.holidayAndTimeOff);
  const language = useAppSelector((state) => state.benguide.data?.language);
  const defaultRichMedia = useAppSelector(
    (state) => state.holidayAndTimeOff.defaultRichMedia.data
  );
  const { isTablet } = useAppSelector((state) => state.layout);

  const { masterId, isEditMode } = benguide;
  const defaultContent = '';
  const {
    id,
    webLink,
    documents = {},
    benefitKind,
    benefitCarrier = {} as BenefitCarrier,
  } = plan;
  const { benefitDescription = '' } = benefitCarrier;
  const richMediaForPlan = get(richMedia, id);
  const { content, inProgress: isRichMediaLoading } = richMediaForPlan || {};
  const planDocument = documents[PLAN_DOCUMENT];

  const isContentReceived = content !== undefined;

  const [editorContent, setEditorContent] = useState<string>(defaultContent);
  const planCardEditorRef = useRef<any>();
  const [isVisible, setIsVisible] = useState(false);
  const [
    isSavePlanDescriptionModalVisible,
    setIsSavePlanDescriptionModalVisible,
  ] = useState<boolean>(false);
  const loadContent = useCallback(() => {
    dispatch(getRichMediaContent(masterId, id, benefitKind));
  }, [dispatch, masterId, id, benefitKind]);

  const showAddContentBtn = useMemo(() => {
    return isEmpty(content) && isEditMode;
  }, [content, isEditMode]);

  const saveContent = (content: string) => {
    dispatch(
      updateRichMediaContent(masterId, id, content, benefitKind, () => {
        dispatch(
          reloadBenefitAndTimeOffSection(
            benguide.masterId,
            benguide.latestRevision
          )
        );
        dispatch(
          fetchHolidayAndTimeOffPlanList(
            benguide.employerId,
            benguide.planYearId,
            benguide.benefitClasses
          )
        );
        setIsSavePlanDescriptionModalVisible(false);
      })
    );
  };

  useEffect(() => {
    loadContent();
  }, [loadContent, plan?.description]);

  useEffect(() => {
    setEditorContent(content || defaultContent);
  }, [content]);

  const getDefaultRichMediaForSection = () => {
    let sectionName = SectionName.HOLIDAY;
    if (benefitKind === benefitCategory.FLEXIBLE_WORKING_HOURS.value) {
      sectionName = SectionName.FLEXIBLE_WORKING_HOURS;
    } else if (benefitKind === benefitCategory.OTHER_TIME_OFF.value) {
      sectionName = SectionName.OTHER_TIME_OFF;
    } else if (benefitKind === benefitCategory.PAID_TIME_OFF.value) {
      sectionName = SectionName.PAID_TIME_OFF;
    } else if (benefitKind === benefitCategory.SICK.value) {
      sectionName = SectionName.SICK;
    }
    if (sectionName && defaultRichMedia[sectionName]) {
      return defaultRichMedia[sectionName];
    }
    return '';
  };

  const handleDescriptionEditSubmit = (content: string) => {
    const isDescriptionEdited = plan && plan.description !== content;
    // We dont saveContent() if description is not edited as it is useless API call
    if (isDescriptionEdited) {
      setIsSavePlanDescriptionModalVisible(true);
    }
  };

  const renderHoverMoveOverlay = () => (
    <div className={styles.moveOverlay}>
      {/* Top Section */}
      {!hideTop ? (
        <div className={styles.top} onClick={moveTop}>
          <LeftArrowIcon className={styles.topIcon} />
          <label>Move</label>
        </div>
      ) : (
        <div className={styles.emptyDiv}></div>
      )}

      {/* Bottom Section */}
      {!hideBottom ? (
        <div className={styles.bottom} onClick={moveBottom}>
          <label>Move</label>
          <RightArrowIcon className={styles.bottomIcon} />
        </div>
      ) : (
        <div className={styles.emptyDiv}></div>
      )}
    </div>
  );

  const handleResetDefault = () => {
    const defaultContent =
      benefitDescription || getDefaultRichMediaForSection();
    setEditorContent(defaultContent);
    planCardEditorRef.current.changeContent(defaultContent);
  };

  const setCurrentConetnt = () => {
    setEditorContent(content);
  };

  return (
    <div className={styles.holidayAndTimeOffCardWrapper}>
      {renderReorder && (
        <div className={styles.moveWrapper}>
          <EditableHotspot
            alwaysVisible={false}
            className={styles.hotSpotHoverContainer}
            customModal={() => <></>}
            overlayComponent={renderHoverMoveOverlay()}
          >
            <div className={styles.moveArea}></div>
          </EditableHotspot>
        </div>
      )}
      <Row>
        <Col span={24}>
          <div className={styles.summaryWrapper}>
            <EditableHotspot
              alwaysVisible={false}
              editText="Edit"
              editIcon={<img src={IconEdit} alt="edit" />}
              useChannel={{
                client: upClient,
                channel: HOLIDAY_AND_TIMEOFF_CHANNEL,
                event: OPEN_HOLIDAY_AND_TIMEOFF_EDIT_MODAL,
                data: plan,
              }}
              dataTestId={`holidayAndTimeOffCard${dataIndex}-name`}
            >
              <div className={styles.summary}>
                <div className={styles.icon}>
                  <img
                    src={get(BENEFIT_KIND, plan.benefitKind).icon}
                    alt={plan.benefitKind}
                  />
                </div>
                <div className={styles.planName}>{plan.name}</div>
                {(!isEmpty(plan.documents) ||
                  !isEmpty(plan.additionalDocuments) ||
                  !isEmpty(plan.additionalWeblinks)) &&
                  !isTablet && (
                    <div
                      className={styles.viewDetailsLink}
                      onClick={() => setIsVisible(true)}
                    >
                      {getText(PLAN_RESOURCES, language)}
                    </div>
                  )}
              </div>
            </EditableHotspot>
          </div>
        </Col>
      </Row>
      <div
        className={
          showAddContentBtn ? styles.addTextWrapper : styles.description
        }
      >
        {!isContentReceived ? (
          <Spinner />
        ) : (
          <EditableHotspot
            className={showAddContentBtn ? styles.addTextHotspot : ''}
            alwaysVisible={showAddContentBtn}
            editText={showAddContentBtn ? '' : 'Edit'}
            editIcon={
              showAddContentBtn ? <></> : <img src={IconEdit} alt="edit" />
            }
            customModal={(
              visible: boolean,
              onClose: () => {},
              editorKey: string
            ) => (
              <PlanCardEditor
                isOpenModal={visible}
                closeModal={onClose}
                editorContent={editorContent}
                isResetEnabled={true}
                resetToCurrentValue={() => {}}
                resetToDefault={handleResetDefault}
                defaultContent={getDefaultRichMediaForSection()}
                updateEditorContent={setEditorContent}
                benguideId={masterId}
                onSubmit={handleDescriptionEditSubmit}
                ref={planCardEditorRef}
                setCurrentConetnt={setCurrentConetnt}
                editorKey={editorKey}
              />
            )}
            dataTestId={`holidayAndTimeOffCard${dataIndex}-description`}
          >
            {showAddContentBtn ? (
              <AddYourText />
            ) : (
              <div className={styles.content}>
                <ContentPreviewer
                  htmlContent={
                    isEmpty(editorContent) ? defaultContent : editorContent
                  }
                />
              </div>
            )}
            {(webLink || planDocument) && isTablet && (
              <div
                className={styles.viewDetailsLink}
                onClick={() => setIsVisible(true)}
              >
                {getText(PLAN_RESOURCES, language)}
              </div>
            )}
          </EditableHotspot>
        )}
      </div>
      <ViewResourcesModal
        plan={plan}
        isVisible={isVisible}
        setIsVisible={setIsVisible}
        benefitKind={benefitKind}
      />
      <SavePlanDescriptionModal
        confirmLoading={isRichMediaLoading}
        isVisible={isSavePlanDescriptionModalVisible}
        onClose={() => {
          setIsSavePlanDescriptionModalVisible(false);
          setEditorContent(plan?.description || '');
        }}
        onConfirm={() => saveContent(editorContent)}
      />
    </div>
  );
};
export default HolidayAndTimeOffCard;
